import React from 'react';
import type { WorkspaceMode } from './config';
import {
  DEFAULT_CODING_SCREEN_CONFIG,
  DEFAULT_GRADING_SCREEN_CONFIG
} from './config';
import type {
  CodingScreenApi,
  CodingScreenState,
  PanelContent
} from './interface';
import { CodingScreenProvider } from './interface';
import { CaseNotesActivityPanelProvider } from './molecules/CaseNotesActivityPanelContext';
import { DEFAULT_SLOT_REGISTRY, SlotRegistryProvider } from './registry';
import { CodingScreenTemplate } from './templates/CodingScreenTemplate';

/**
 * The public mount point: supplies its OWN `CodingScreenProvider` -- the shell
 * is the ONE place in this folder that constructs a config and hands it down; every descendant
 * reads it back out via `useCodingConfig()`. Accepts OPTIONAL `state`/`api`/`panelContent` props and
 * forwards them into that provider; all are `undefined` when omitted (the empty shell renders fine
 * with none supplied). A dashboard-ui-side adapter (living OUTSIDE this folder, so it may see
 * redux/Apollo) maps real values from the app and injects them through these props.
 *
 * `mode` (default `'coding'`) selects which hard-coded default config to render from -- the SAME
 * shell serves both the coding bench and CQA grading (grading is a bench MODE, not a
 * separate screen). The empty shell renders identically either way; only `CodingScreenConfig.mode`
 * (and therefore `CodingScreenTemplate`'s `data-mode` attribute) differs. A routed caller passes the
 * prop via `render={() => <CodingScreenShell mode="grading" />}` (a `<Route component=...>` cannot
 * pass props) -- see routing.ts.
 */
export const CodingScreenShell: React.FC<{
  mode?: WorkspaceMode;
  state?: CodingScreenState;
  api?: CodingScreenApi;
  panelContent?: PanelContent;
}> = ({ mode = 'coding', state, api, panelContent }) => {
  return (<SlotRegistryProvider registry={DEFAULT_SLOT_REGISTRY}>
    <CodingScreenProvider
      config={
        mode === 'grading'
          ? DEFAULT_GRADING_SCREEN_CONFIG
          : DEFAULT_CODING_SCREEN_CONFIG
      }
      state={state}
      api={api}
      panelContent={panelContent}
    >
      <CaseNotesActivityPanelProvider>
        <CodingScreenTemplate />
      </CaseNotesActivityPanelProvider>
    </CodingScreenProvider>
  </SlotRegistryProvider>)
};

export default CodingScreenShell;
